import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import { setFilter, resetFilters, resetCharts } from 'src/redux/Dashboard/Charts/ChartsSlice'
import { getFilters } from 'src/services/KPI/KPIForm/KPIForm.service'
import { IFilter } from 'src/redux/Dashboard/Charts/Charts.interface'
import { useAlert } from 'src/hooks/useAlert'
import { getChartKPI } from 'src/services/KPI/Charts/Charts.service'
import useLoading from 'src/hooks/useLoading'

/**
 * Custom hook `useChart` para manejar la lógica relacionada con la carga de gráficos y filtros en el dashboard.
 * Este hook proporciona funciones para cargar gráficos, aplicar filtros, y restablecer gráficos y filtros.
 * 
 * @returns {Object} Un objeto con las propiedades y funciones necesarias para manejar gráficos y filtros.
 */
export const useChart = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const { filters, filter, charts } = useSelector(
    (state: RootState) => state.kpiChart,
  )

  /**
   * Función para cargar los gráficos basados en el filtro actual.
   * Muestra alertas dependiendo del estado de la carga (éxito o error).
   * 
   * @async
   * @function
   * @returns {Promise<void>} Una promesa que maneja la carga de gráficos.
   * 
   * @example
   * ```ts
   * loadCharts()
   * ```
   */
  const loadCharts = () => {
    try {
      startLoading()
      dispatch(getChartKPI(filter))
        .then((result: any) => {
          if (getChartKPI.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las Gráficas',
            )
          } else if (getChartKPI.fulfilled.match(result)) {
            showAlert('success', 'Gráficas cargadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Función para cargar los filtros disponibles.
   * Muestra alertas dependiendo del estado de la carga (éxito o error).
   * 
   * @async
   * @function
   * @returns {Promise<void>} Una promesa que maneja la carga de filtros.
   * 
   * @example
   * ```ts
   * loadFilters()
   * ```
   */
  const loadFilters = () => {
    try {
      startLoading()
      dispatch(getFilters({})).finally(() => {
        stopLoading()
      })
      showAlert('success', 'Filtros cargados correctamente')
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Función para aplicar filtros al formulario.
   * 
   * @param {IFilter} filters - Los filtros a aplicar.
   * 
   * @example
   * ```ts
   * setFilterForm({ key: 'value' })
   * ```
   */
  const setFilterForm = (filters: IFilter) => {
    dispatch(setFilter(filters))
  }

  /**
   * Función para restablecer los filtros de KPI.
   * 
   * @example
   * ```ts
   * resetFiltersKPI()
   * ```
   */
  const resetFiltersKPI = () => {
    dispatch(resetFilters())
  }

  /**
   * Función para restablecer los gráficos de KPI.
   * 
   * @example
   * ```ts
   * resetChartsKPI()
   * ```
   */
  const resetChartsKPI = () => {
    dispatch(resetCharts())
  }

  return {
    charts,
    filters,
    filter,
    loadCharts,
    setFilterForm,
    loadFilters,
    resetFiltersKPI,
    resetChartsKPI
  }
}
